<template>
  <div class="fullscreen">
    <div
      v-if="isLoading"
      class="fullscreen d-flex justify-center align-center"
    >
      <v-progress-circular
        :size="100"
        color="primary"
        indeterminate
      />
    </div>
    <slot v-if="hasLoaded" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onBeforeMount } from 'vue';
import { EndpointMode, EndpointType } from '@/api/out';
import { goToErrorPage } from '@/router';
import { DatasetRouterParams, ErrorKey } from '@/router/model';
import DatasetStore from '@/stores/dataset/dataset-module';
import StationDatasetStore from '@/stores/dataset/station-dataset-module';
import RasterDatasetStore from '@/stores/dataset/raster-dataset-module';
import { loadStoresFromDatasetParams } from '@/stores/dataset/dataset-store-utils';

export default defineComponent({
  name: 'DatasetWrapper',

  props: {
    type: {
      type: String,
      default: 'timeseries',
    },
    mode: {
      type: String,
      required: true,
    },
    resourceId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const datasetStore = DatasetStore;
    const stationDatasetStore = StationDatasetStore;
    const rasterDatasetStore = RasterDatasetStore;
    onBeforeMount((): void => {
      if (props.mode !== EndpointMode.Historical) {
        console.error(
          `Only historical datasets are supported at the moment, but got '${props.mode}'`
        );
        goToErrorPage(ErrorKey.UNKNOWN_PATH);
      }

      const datasetParams: DatasetRouterParams = {
        resourceId: props.resourceId,
        mode: EndpointMode.Historical,
        type: props.type as EndpointType,
      };

      loadStoresFromDatasetParams(datasetParams);
    });

    const isLoading = computed((): boolean => {
      if (!datasetStore.isLoading) return false;
      if (props.type === EndpointType.Station) return stationDatasetStore.isLoading;
      return rasterDatasetStore.isLoading;
    });

    const hasLoaded = computed((): boolean => {
      if (!datasetStore.hasLoaded) return false;
      if (props.type === EndpointType.Station) return stationDatasetStore.hasLoaded;
      return rasterDatasetStore.hasLoaded;
    });

    return {
      isLoading,
      hasLoaded,
    };
  },
});
</script>

<style>
.fullscreen {
  height: 100%;
  width: 100%;
  background-color: rgb(var(--v-theme-secondary));
}
</style>